import { memo, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import WhatsAppShareButton from "app/pages/.shared/social/whatapps/WhatsAppShareButton";
import MessengerShareLink from "app/pages/.shared/social/facebook/MessengerShareLink";
import CopyToClipboardButton from "app/pages/.shared/CopyToClipboardButton/CopyToClipboardButton";
import MailToButton from "app/pages/.shared/MailTo/MailToButton";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { resolutionPropType } from "app/utils/propTypes";
import { RESOLUTION } from "app/constants";
import { stringify } from "qs";
import "./SocialBar.scss";
import AppGlobalsContext from "app/AppGlobalsContext";

const SocialBar = ({
	urlToShare,
	resolution,
	partnerData = {},
	brandDisplayName,
	showPfsMessage,
}) => {
	const { track } = useAnalytics();

	const intl = useIntl();
	const { envVars } = useContext(AppGlobalsContext);

	const facebookAppId = envVars.FACEBOOK_APP_ID;

	const onCopySuccess = useCallback(() => {
		track("share_product_page", { channel: "clipboard" });
	}, []);

	const onMailShare = useCallback(() => {
		track("share_product_page", { channel: "email" });
	}, []);

	const onWhatsAppShare = useCallback(() => {
		track("share_product_page", { channel: "whatsapp" });
	}, []);

	const onMessengerShare = useCallback(() => {
		track("share_product_page", { channel: "messenger" });
	}, []);

	const partnerDataQuery = stringify(partnerData);

	const emailSubject = intl.formatMessage({ id: "share.email.subject" });
	const emailBody = intl.formatMessage(
		{ id: "share.email.body" },
		{
			url: `${urlToShare}?source=share_email%26${partnerDataQuery}`,
			brandName: showPfsMessage
				? intl.formatMessage(
						{
							id: "general.perfectstay.partnership.name",
						},
						{ brandName: brandDisplayName }
					)
				: brandDisplayName,
		}
	);

	return (
		<div className="social-bar">
			<div className="social-bar__title">
				<FormattedMessage id="share.title.label" />
			</div>
			<div className="social-bar__body">
				{(resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM) && (
					<div className="social-bar__item">
						<WhatsAppShareButton
							onClick={onWhatsAppShare}
							label={<FormattedMessage id="share.whatsapp.button.label" />}
							text={`${urlToShare}?source=share_whatsapp%26${partnerDataQuery}`}
						/>
					</div>
				)}
				{facebookAppId && (
					<div className="social-bar__item">
						<MessengerShareLink
							resolution={resolution}
							onClick={onMessengerShare}
							label={<FormattedMessage id="share.messenger.button.label" />}
							link={`${urlToShare}?source=share_messenger&${partnerDataQuery}`}
							facebookAppID={facebookAppId}
						/>
					</div>
				)}
				<div className="social-bar__item">
					<MailToButton
						subject={emailSubject}
						onClick={onMailShare}
						label={<FormattedMessage id="share.email.button.label" />}
						body={emailBody}
					/>
				</div>
				<div className="social-bar__item">
					<CopyToClipboardButton
						onCopySuccess={onCopySuccess}
						label={<FormattedMessage id="share.clipboard.button.label" />}
						text={`${urlToShare}?source=share_copylink&${partnerDataQuery}`}
					/>
				</div>
			</div>
		</div>
	);
};

SocialBar.propTypes = {
	resolution: resolutionPropType,
	urlToShare: PropTypes.string.isRequired,
	partnerData: PropTypes.object,
	brandDisplayName: PropTypes.string,
	showPfsMessage: PropTypes.bool,
};

export default memo(SocialBar);
